import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Stack,
  Chip,
  Tooltip,
  useTheme,
  alpha,
  LinearProgress,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
  Info as InfoIcon,
} from '@mui/icons-material';

interface KPIMetric {
  label: string;
  value: string | number;
  unit?: string;
  change?: number;
  changeLabel?: string;
  target?: number;
  icon?: React.ReactNode;
  color?: string;
  description?: string;
  format?: 'number' | 'currency' | 'percentage' | 'custom';
  precision?: number;
}

interface KPIProps {
  metrics: KPIMetric | KPIMetric[];
  variant?: 'default' | 'compact' | 'detailed' | 'minimal';
  layout?: 'horizontal' | 'vertical' | 'grid';
  showTrend?: boolean;
  showProgress?: boolean;
  clickable?: boolean;
  onClick?: (metric: KPIMetric) => void;
}

/**
 * Format value based on type
 */
const formatValue = (
  value: string | number,
  format?: string,
  precision: number = 0
): string => {
  if (typeof value === 'string') return value;

  switch (format) {
    case 'currency':
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: precision,
        maximumFractionDigits: precision,
      }).format(value);
    case 'percentage':
      return `${value.toFixed(precision)}%`;
    case 'number':
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: precision,
        maximumFractionDigits: precision,
      }).format(value);
    default:
      return value.toString();
  }
};

/**
 * Single KPI Card component
 */
const KPICard: React.FC<{
  metric: KPIMetric;
  variant: 'default' | 'compact' | 'detailed' | 'minimal';
  showTrend: boolean;
  showProgress: boolean;
  clickable: boolean;
  onClick?: (metric: KPIMetric) => void;
}> = ({
  metric,
  variant,
  showTrend,
  showProgress,
  clickable,
  onClick,
}) => {
  const theme = useTheme();

  const getTrendIcon = () => {
    if (!metric.change) return <TrendingFlatIcon />;
    if (metric.change > 0) return <TrendingUpIcon />;
    return <TrendingDownIcon />;
  };

  const getTrendColor = () => {
    if (!metric.change) return theme.palette.text.secondary;
    if (metric.change > 0) return theme.palette.success.main;
    return theme.palette.error.main;
  };

  const progress = metric.target
    ? Math.min(100, (Number(metric.value) / metric.target) * 100)
    : 0;

  const handleClick = () => {
    if (clickable && onClick) {
      onClick(metric);
    }
  };

  if (variant === 'minimal') {
    return (
      <Box
        onClick={handleClick}
        sx={{
          cursor: clickable ? 'pointer' : 'default',
          '&:hover': clickable ? {
            transform: 'translateY(-2px)',
            transition: 'transform 0.2s',
          } : {},
        }}
      >
        <Stack direction="row" alignItems="baseline" spacing={1}>
          <Typography variant="h4" sx={{ fontWeight: 600, color: metric.color || 'text.primary' }}>
            {formatValue(metric.value, metric.format, metric.precision)}
          </Typography>
          {metric.unit && (
            <Typography variant="body2" color="text.secondary">
              {metric.unit}
            </Typography>
          )}
        </Stack>
        <Typography variant="body2" color="text.secondary">
          {metric.label}
        </Typography>
      </Box>
    );
  }

  return (
    <Card
      onClick={handleClick}
      sx={{
        height: '100%',
        cursor: clickable ? 'pointer' : 'default',
        transition: 'all 0.3s ease',
        '&:hover': clickable ? {
          transform: 'translateY(-4px)',
          boxShadow: 4,
        } : {},
        borderTop: metric.color ? `4px solid ${metric.color}` : 'none',
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontWeight: 500 }}
              >
                {metric.label}
              </Typography>
              {metric.description && (
                <Tooltip title={metric.description}>
                  <InfoIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                </Tooltip>
              )}
            </Box>
            
            <Stack direction="row" alignItems="baseline" spacing={1}>
              <Typography
                variant={variant === 'compact' ? 'h5' : 'h4'}
                sx={{
                  fontWeight: 600,
                  color: metric.color || 'text.primary',
                }}
              >
                {formatValue(metric.value, metric.format, metric.precision)}
              </Typography>
              {metric.unit && (
                <Typography variant="body1" color="text.secondary">
                  {metric.unit}
                </Typography>
              )}
            </Stack>
          </Box>

          {metric.icon && (
            <Box
              sx={{
                p: 1,
                borderRadius: 1,
                backgroundColor: alpha(metric.color || theme.palette.primary.main, 0.1),
                color: metric.color || theme.palette.primary.main,
              }}
            >
              {metric.icon}
            </Box>
          )}
        </Box>

        {showTrend && metric.change !== undefined && (
          <Box sx={{ mb: variant === 'detailed' ? 2 : 0 }}>
            <Chip
              size="small"
              icon={getTrendIcon()}
              label={`${metric.change > 0 ? '+' : ''}${metric.change.toFixed(1)}%${
                metric.changeLabel ? ` ${metric.changeLabel}` : ''
              }`}
              sx={{
                backgroundColor: alpha(getTrendColor(), 0.1),
                color: getTrendColor(),
                fontWeight: 500,
                '& .MuiChip-icon': {
                  color: getTrendColor(),
                },
              }}
            />
          </Box>
        )}

        {showProgress && metric.target && (
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                Progress
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {progress.toFixed(0)}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 6,
                borderRadius: 1,
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                '& .MuiLinearProgress-bar': {
                  backgroundColor: metric.color || theme.palette.primary.main,
                },
              }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
              Target: {formatValue(metric.target, metric.format, metric.precision)}
            </Typography>
          </Box>
        )}

        {variant === 'detailed' && metric.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            {metric.description}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

/**
 * KPI component for displaying key performance indicators
 * Supports single or multiple metrics with various display options
 */
export const KPI: React.FC<KPIProps> = ({
  metrics,
  variant = 'default',
  layout = 'horizontal',
  showTrend = true,
  showProgress = false,
  clickable = false,
  onClick,
}) => {
  const theme = useTheme();
  const metricsArray = Array.isArray(metrics) ? metrics : [metrics];

  if (layout === 'grid') {
    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: `repeat(${Math.min(4, metricsArray.length)}, 1fr)`,
          },
          gap: 3,
        }}
      >
        {metricsArray.map((metric, index) => (
          <KPICard
            key={index}
            metric={metric}
            variant={variant}
            showTrend={showTrend}
            showProgress={showProgress}
            clickable={clickable}
            onClick={onClick}
          />
        ))}
      </Box>
    );
  }

  if (layout === 'vertical') {
    return (
      <Stack spacing={3}>
        {metricsArray.map((metric, index) => (
          <KPICard
            key={index}
            metric={metric}
            variant={variant}
            showTrend={showTrend}
            showProgress={showProgress}
            clickable={clickable}
            onClick={onClick}
          />
        ))}
      </Stack>
    );
  }

  // Horizontal layout (default)
  return (
    <Stack direction="row" spacing={3} sx={{ overflowX: 'auto', pb: 1 }}>
      {metricsArray.map((metric, index) => (
        <Box key={index} sx={{ minWidth: 250, flex: 1 }}>
          <KPICard
            metric={metric}
            variant={variant}
            showTrend={showTrend}
            showProgress={showProgress}
            clickable={clickable}
            onClick={onClick}
          />
        </Box>
      ))}
    </Stack>
  );
};

export default KPI;